import { useEffect, useState, lazy, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { CheckIn, Event, CellarTobacco } from '../lib/supabase';
import { useEmberStore } from '../lib/store';
import { Flame, Plus, Compass, User, LogOut, Calendar, Check } from 'lucide-react';
import BottomNav from '../components/BottomNav';
import { SkeletonCheckInCard, SkeletonEventCard } from '../components/SkeletonLoader';
import Button from '../components/ui/Button';

const CheckInCarousel = lazy(() => import('../components/CheckInCarousel'));
const EventCarousel = lazy(() => import('../components/EventCarousel'));
const EventPopup = lazy(() => import('../components/EventPopup'));
const NotificationBell = lazy(() => import('../components/NotificationBell'));
const ConnectionsList = lazy(() => import('../components/ConnectionsList'));

export default function Home() {
  const navigate = useNavigate();
  const { currentUser, checkIns, setCheckIns } = useEmberStore();
  const [loading, setLoading] = useState(true);
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [reminders, setReminders] = useState<CellarTobacco[]>([]);
  const FEEDBACK_PROMPT_KEY = 'public_beta_feedback_v1';
  const UPDATE_NOTIFICATION_TITLE = 'Ember Update (Last 2 Weeks)';
  const ADD_MORE_FEATURE_TITLE = 'Add More to Batches';
const supabaseClientRef = { current: null as any };
const getSupabase = async () => {
  if (supabaseClientRef.current) return supabaseClientRef.current;
  const mod = await import('../lib/supabase');
  supabaseClientRef.current = mod.supabase;
  return supabaseClientRef.current;
};

  useEffect(() => {
    loadCheckIns();
    loadUpcomingEvents();
    loadReminders();
  }, []);

  // Ensure each user gets a one-off feedback notification
  useEffect(() => {
    if (!currentUser) return;

    (async () => {
      try {
        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from('notifications')
          .select('id')
          .eq('user_id', currentUser.id)
          .eq('type', 'system')
          .eq('title', 'Thoughts?!')
          .contains('data', { feedback_prompt_key: FEEDBACK_PROMPT_KEY })
          .limit(1);

        if (error) {
          console.warn('Feedback notification check failed', error);
          return;
        }

        if (!data || data.length === 0) {
          await supabase.from('notifications').insert({
            user_id: currentUser.id,
            type: 'system',
            title: 'Thoughts?!',
            message: 'I’d love any feedback you have on Ember so far. Tap to share a quick rating and comment.',
            data: { feedback_prompt_key: FEEDBACK_PROMPT_KEY },
          });
        }
      } catch (err) {
        console.warn('Feedback notification setup failed', err);
      }
    })();
  }, [currentUser]);

  // Show a one-off "Ember Update" system notification with recent changes
  useEffect(() => {
    if (!currentUser) return;

    (async () => {
      try {
        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from('notifications')
          .select('id')
          .eq('user_id', currentUser.id)
          .eq('type', 'system')
          .eq('title', UPDATE_NOTIFICATION_TITLE)
          .limit(1);

        if (error) {
          console.warn('Update notification check failed', error);
          return;
        }

        if (!data || data.length === 0) {
          await supabase.from('notifications').insert({
            user_id: currentUser.id,
            type: 'system',
            title: UPDATE_NOTIFICATION_TITLE,
            message:
              'Better cellar tracking (dates, storage, opened tins).\n\n' +
              'Improved check-ins, sessions, and event cards.\n\n' +
              'Smoother dark mode + cleaner Home.\n\n' +
              'New “Thoughts?!” feedback button — tell me how Ember feels.\n\n' +
              'Thanks for being here — your feedback genuinely drives the app. 🔥',
          });
        }
      } catch (err) {
        console.warn('Update notification setup failed', err);
      }
    })();
  }, [currentUser]);

  // Show notification about new "Add More" feature
  useEffect(() => {
    if (!currentUser) return;

    (async () => {
      try {
        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from('notifications')
          .select('id')
          .eq('user_id', currentUser.id)
          .eq('type', 'system')
          .eq('title', ADD_MORE_FEATURE_TITLE)
          .limit(1);

        if (error) {
          console.warn('Add More notification check failed', error);
          return;
        }

        if (!data || data.length === 0) {
          await supabase.from('notifications').insert({
            user_id: currentUser.id,
            type: 'system',
            title: ADD_MORE_FEATURE_TITLE,
            message: 'Add more items to your existing tobacco batches! Tap "Add More" in any tobacco detail modal to quickly add duplicates with pre-filled information. New items automatically join the same batch.',
          });
        }
      } catch (err) {
        console.warn('Add More notification setup failed', err);
      }
    })();
  }, [currentUser]);

  async function loadCheckIns() {
    try {
      const supabase = await getSupabase();
      const { data, error } = await supabase
        .from('checkins')
        .select(`
          *,
          tobacco:tobaccos(*),
          venue:venues(*),
          cellar_tobacco:cellar_tobacco(*),
          cellar_pipe:cellar_pipes(*),
          cellar_lighter:cellar_lighters(*),
          profile:profiles!checkins_user_id_fkey(username, is_profile_public)
        `)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      if (data) setCheckIns(data as any);
    } catch (err) {
      console.error('Error loading check-ins:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadUpcomingEvents() {
    try {
      const now = new Date();
      // Add 5-minute buffer to account for timing differences and timezone conversions
      const bufferTime = new Date(now.getTime() - 5 * 60 * 1000);

      const supabase = await getSupabase();
      const { data, error } = await supabase
        .from('events')
        .select(`
          *,
          venue:venues(*),
          creator:profiles!events_creator_id_fkey(username),
          responses:event_responses(*)
        `)
        .gte('start_datetime', bufferTime.toISOString())
        .order('start_datetime', { ascending: true })
        .limit(10);

      if (error) throw error;
      if (data) {
        // Filter events based on user responses
        const filteredEvents = data.filter((event: any) => {
          if (!currentUser) return true;
          
          const userResponse = event.responses?.find((r: { user_id: string; response_type: string }) => r.user_id === currentUser.id);
          // Show if no response, attending, or interested (hide if not_interested)
          return !userResponse || userResponse.response_type !== 'not_interested';
        });
        
        setUpcomingEvents(filteredEvents.slice(0, 3) as Event[]);
      }
    } catch (err) {
      console.error('Error loading upcoming events:', err);
    }
  }

  async function handleLogout() {
    const supabase = await getSupabase();
    await supabase.auth.signOut();
    // Let the routing logic handle redirect based on auth state
  }

  async function loadReminders() {
    try {
      const supabase = await getSupabase();
      const { data } = await supabase
        .from('cellar_tobacco')
        .select('*')
        .not('reminder_at', 'is', null)
        .order('reminder_at', { ascending: true });
      if (data) setReminders(data as any);
    } catch (err) {
      // Silent fail if column not available yet
      console.warn('Reminders load skipped:', err);
    }
  }

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '120px' }}>
      {/* Header */}
      <header style={{
        padding: '1rem',
        borderBottom: '1px solid #1f1f1f',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        backgroundColor: '#0b0b0b',
        zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Flame size={32} color="var(--color-accent)" />
          <h1 style={{ fontSize: '1.8rem', letterSpacing: '0.02em', textTransform: 'uppercase' }}>Ember</h1>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Link
            to="/updates"
            style={{
              color: 'var(--color-accent)',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
            }}
          >
            Updates
          </Link>
          <Suspense fallback={null}>
            <NotificationBell />
          </Suspense>
          <button
            onClick={handleLogout}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-accent)',
              cursor: 'pointer',
            }}
          >
            <LogOut size={24} />
          </button>
        </div>
      </header>

      {/* Upcoming Events - Always visible */}
      <div className="container" style={{ marginBottom: '1rem' }}>
        <div
          className="card"
          style={{
            border: '1px solid var(--color-accent)',
            padding: '1rem',
            // Ember stock background to make the section more engaging
            // Slightly lighter overlay so the image is visible in both dark and high-contrast modes
            backgroundImage: "linear-gradient(135deg, rgba(11,11,11,0.78), rgba(11,11,11,0.55)), url('/ember-bg.jpg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <Calendar size={20} color="var(--color-accent)" />
            <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#ffffff' }}>
              Upcoming Events
            </h3>
          </div>
          
          {upcomingEvents.length === 0 ? (
            <p style={{ color: '#999', fontSize: '0.875rem', textAlign: 'center', padding: '1rem 0' }}>
              No upcoming events
            </p>
          ) : (
            <>
              <Suspense fallback={<SkeletonEventCard />}>
                <EventCarousel 
                  events={upcomingEvents.slice(0, 5)} 
                  currentUserId={currentUser?.id}
                  onEventClick={(event) => setSelectedEvent(event)}
                />
              </Suspense>
              {upcomingEvents.length > 5 && (
                <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
                  <Link to="/discover">
                    <Button
                      variant="primary"
                      fullWidth
                      style={{ color: '#ffffff' }}
                    >
                      Show All Events ({upcomingEvents.length})
                    </Button>
                  </Link>
                </div>
              )}
            </>
          )}
          
          {/* Always show "Show All Events" link to Discover page */}
          {upcomingEvents.length > 0 && upcomingEvents.length <= 5 && (
            <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
              <Link to="/discover">
                <Button
                  variant="primary"
                  fullWidth
                  style={{ color: '#ffffff' }}
                >
                  Discover More Events
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Check-ins Feed */}
      <div className="container">
        <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>Recent Check-ins</h3>
        
        {loading ? (
          <>
            <SkeletonCheckInCard />
            <SkeletonCheckInCard />
          </>
        ) : checkIns.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ color: '#d3de5e' }}>No check-ins yet. Be the first!</p>
            <Link to="/checkin">
              <Button variant="primary" style={{ marginTop: '1rem' }}>
                Create Check-in
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <Suspense fallback={<SkeletonCheckInCard />}>
              <CheckInCarousel 
                checkIns={checkIns.slice(0, 5)} 
                onDelete={() => loadCheckIns()}
              />
            </Suspense>
            
            {checkIns.length > 5 && (
              <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                <Link to="/checkins">
                  <Button variant="secondary" fullWidth>
                    Show All Check-ins ({checkIns.length})
                  </Button>
                </Link>
              </div>
            )}
          </>
        )}
      </div>

      {/* Reminders - Carousel */}
      <div className="container" style={{ marginTop: '1.5rem' }}>
        <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>Upcoming Reminders</h3>
        {reminders.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
            <p style={{ color: '#999', fontSize: '0.875rem' }}>No reminders set yet.</p>
          </div>
        ) : (
          <div
            className="event-carousel-scrollbar"
            style={{
              display: 'flex',
              overflowX: 'auto',
              scrollSnapType: 'x mandatory',
              gap: '1rem',
              paddingBottom: '1rem',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
            }}
          >
            {reminders.map((r) => (
              <div
                key={r.id}
                className="carousel-item"
                style={{
                  minWidth: 'min(320px, 85vw)',
                  maxWidth: 'min(320px, 85vw)',
                  flexShrink: 0,
                  scrollSnapAlign: 'start',
                }}
              >
                <a
                  href="/cellar#reminder"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div
                    className="card"
                    style={{
                      border: '1px solid var(--color-secondary)',
                    }}
                  >
                    <div style={{ fontSize: '0.85rem', color: 'var(--color-secondary)', marginBottom: '0.4rem' }}>
                      🔔 Reminder
                    </div>
                    <div style={{ fontWeight: 600, color: 'var(--color-secondary)', marginBottom: '0.25rem' }}>
                      {r.brand} - {r.blend_name}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--color-secondary)', marginBottom: '0.5rem' }}>
                      {new Date(r.reminder_at as string).toLocaleDateString()}
                    </div>
                    {r.reminder_notes && (
                      <p
                        style={{
                          fontSize: '0.8rem',
                          color: 'var(--color-text)',
                          margin: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {r.reminder_notes}
                      </p>
                    )}
                  </div>
                </a>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Connections */}
      <div className="container" style={{ marginTop: '1.5rem' }}>
        <Suspense
          fallback={(
            <div className="card" style={{ padding: '1rem' }}>
              <p style={{ color: '#999' }}>Loading connections...</p>
            </div>
          )}
        >
          <ConnectionsList />
        </Suspense>
      </div>

      <BottomNav />

      {/* Event Popup */}
      {selectedEvent && (
        <Suspense fallback={null}>
          <EventPopup
            event={selectedEvent}
            onClose={() => setSelectedEvent(null)}
            onResponseUpdate={() => {
              loadUpcomingEvents();
              setSelectedEvent(null);
            }}
          />
        </Suspense>
      )}
    </div>
  );
}

